import { useId, useState } from 'react';

import { defaultCmsBranding } from '@shared/cms/content';
import type { SiteCmsBranding, SiteCmsDraftResponse, SiteCmsTheme } from '@shared/types/entities';
import { Check, Monitor, Smartphone } from 'lucide-react';

import SiteCmsImagePicker from './SiteCmsImagePicker';
import SiteCmsPreview from './SiteCmsPreview';
import { useCmsInteractions, useCmsPendingForm } from './use-cms-interactions';

type Props = { active?: boolean; draft: SiteCmsDraftResponse; onChange: (draft: SiteCmsDraftResponse) => void };
const themes: { id: SiteCmsTheme; title: string; caption: string }[] = [
  { id: 'marine', title: 'Marine', caption: 'Sportif, affirmé & convivial' },
  { id: 'sable', title: 'Sable', caption: 'Chaleureux, éditorial & lumineux' },
  { id: 'ardoise', title: 'Ardoise', caption: 'Structuré, sobre & contemporain' },
];
export default function SiteCmsDesign({ draft, onChange, active = true }: Props) {
  const [mobile, setMobile] = useState(false);
  const [preview, setPreview] = useState(false);
  const [undo, setUndo] = useState<{
    revision: number;
    theme: SiteCmsTheme;
    primary: string;
    accent: string;
    typography: SiteCmsBranding['typography'];
  } | null>(null);
  const { ask } = useCmsInteractions();
  const brand = { ...defaultCmsBranding(draft.theme), ...draft.branding };
  const wide = brand.layoutMode === 'widescreen';
  const update = (values: Partial<SiteCmsBranding>) => {
    setUndo(null);
    onChange({ ...draft, branding: { ...brand, ...values } });
  };
  async function choose(theme: SiteCmsTheme) {
    if (theme === draft.theme) return;
    const choice = await ask({
      title: 'Changer d’ambiance',
      message:
        'Le logo, les images et le contenu seront conservés. Choisissez le traitement de vos couleurs et de votre typographie.',
      choices: [
        { value: 'keep', label: 'Garder ma personnalisation' },
        { value: 'palette', label: 'Appliquer l’ambiance' },
      ],
    });
    if (!choice) return;
    setUndo({
      revision: draft.revision,
      theme: draft.theme,
      primary: brand.primary,
      accent: brand.accent,
      typography: brand.typography,
    });
    const palette = defaultCmsBranding(theme);
    onChange({
      ...draft,
      theme,
      branding:
        choice === 'keep'
          ? brand
          : { ...brand, primary: palette.primary, accent: palette.accent, typography: palette.typography },
    });
  }
  const owner = { ownerType: draft.ownerType, ownerId: draft.ownerId };
  return (
    <div className='cms-design-layout'>
      <div className='cms-stack'>
        <section className='cms-card'>
          <span className='cms-kicker'>Identité</span>
          <h3>Votre logo</h3>
          <p className='cms-muted'>
            Visible dans l’en-tête et le pied de page. L’Admin conserve l’identité BoulesConnect.
          </p>
          <SiteCmsImagePicker
            {...owner}
            label='Logo du site'
            value={brand.logoImageId}
            onChange={logoImageId => update({ logoImageId })}
          />
        </section>
        <section className='cms-card cms-design-layout-choice'>
          <span className='cms-kicker'>Disposition</span>
          <h3>Largeur de votre site</h3>
          <p className='cms-muted'>
            Choisissez la présentation de toutes vos pages. Le contenu reste lisible sur ordinateur comme sur mobile.
          </p>
          <div className='cms-layout-options' role='group' aria-label='Disposition du site'>
            {(
              [
                ['classic', 'Classique', 'Contenu centré, avec fond de site facultatif'],
                ['widescreen', 'Pleine largeur', 'En-tête et sections sur toute la largeur de l’écran'],
              ] as const
            ).map(([id, title, description]) => (
              <button
                key={id}
                type='button'
                className={`cms-layout-option ${(brand.layoutMode ?? 'classic') === id ? 'is-selected' : ''}`}
                aria-pressed={(brand.layoutMode ?? 'classic') === id}
                onClick={() => update({ layoutMode: id })}
              >
                <span className={`cms-layout-sample cms-layout-sample-${id}`} aria-hidden='true'>
                  <span className='cms-layout-sample-header' />
                  <span className='cms-layout-sample-hero' />
                  <span className='cms-layout-sample-section' />
                </span>
                <span className='cms-layout-option-copy'>
                  <b>{title}</b>
                  <small>{description}</small>
                </span>
                {(brand.layoutMode ?? 'classic') === id && <Check size={18} aria-hidden='true' />}
              </button>
            ))}
          </div>
          {wide && (
            <p className='cms-notice'>
              En pleine largeur, le fond global du site est masqué. Votre ancienne image reste enregistrée si vous
              revenez au mode Classique. Le fond du hero, lui, reste disponible dans Pages → Accueil.
            </p>
          )}
        </section>
        {wide ? (
          <details className='cms-card cms-design-inactive-background'>
            <summary>
              <strong>Fond global du site</strong>
              <span>Inactif en pleine largeur — afficher les détails</span>
            </summary>
            <p className='cms-hint'>
              Le fond global n’est pas utilisé en mode Pleine largeur.{' '}
              {brand.backgroundImageId
                ? 'L’image et son cadrage sont conservés dans votre brouillon.'
                : 'Aucune image de fond enregistrée.'}{' '}
              Revenez au mode Classique pour le modifier.
            </p>
          </details>
        ) : (
          <section className='cms-card cms-design-background'>
            <span className='cms-kicker'>Couverture du site</span>
            <h3>Un véritable fond de site</h3>
            <p className='cms-muted'>
              Cette image habille l’arrière-plan de toutes les pages. Le contenu garde des surfaces lisibles au premier
              plan.
            </p>
            <SiteCmsImagePicker
              {...owner}
              label='Image d’arrière-plan'
              value={brand.backgroundImageId ?? null}
              onChange={backgroundImageId => update({ backgroundImageId })}
            />
            <div className='cms-field-label'>
              Cadrage du fond
              <div className='cms-segmented' role='group' aria-label='Cadrage de l’arrière-plan'>
                {(
                  [
                    ['top', 'Haut'],
                    ['center', 'Centre'],
                    ['bottom', 'Bas'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type='button'
                    disabled={!brand.backgroundImageId}
                    aria-pressed={(brand.backgroundPosition ?? 'center') === value}
                    onClick={() => update({ backgroundPosition: value })}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <p className='cms-hint'>
              Facultatif : sans image, le site utilise les couleurs de son ambiance. Le fond est décoratif ; placez les
              informations importantes dans le contenu.
            </p>
          </section>
        )}
        <section className='cms-card'>
          <span className='cms-kicker'>Bannière facultative</span>
          <h3>Une image à côté de votre accueil</h3>
          <p className='cms-muted'>
            L’ancienne « couverture » est conservée ici : elle n’est pas déplacée automatiquement dans le fond.
          </p>
          <SiteCmsImagePicker
            {...owner}
            label='Image de bannière'
            value={brand.coverImageId}
            onChange={coverImageId => update({ coverImageId })}
          />
          <div className='cms-field-label'>
            Cadrage de la bannière
            <div className='cms-segmented' role='group' aria-label='Cadrage de la bannière'>
              {(
                [
                  ['top', 'Haut'],
                  ['center', 'Centre'],
                  ['bottom', 'Bas'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type='button'
                  disabled={!brand.coverImageId}
                  aria-pressed={brand.coverPosition === value}
                  onClick={() => update({ coverPosition: value })}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <p className='cms-hint'>
            Sans bannière, le texte d’accueil occupe l’espace disponible. Une image personnalisée dans l’en-tête de
            l’accueil remplace cette bannière, jamais le fond du site.
          </p>
        </section>
        <section className='cms-card'>
          <span className='cms-kicker'>Ambiance</span>
          <h3>Choisissez votre style</h3>
          <div className='cms-theme-grid' role='group' aria-label='Ambiance du site'>
            {themes.map(theme => (
              <button
                key={theme.id}
                type='button'
                aria-pressed={draft.theme === theme.id}
                className={`cms-theme-card ${draft.theme === theme.id ? 'is-selected' : ''}`}
                onClick={() => void choose(theme.id)}
              >
                <div className={`cms-theme-mini cms-theme-${theme.id}`} aria-hidden='true'>
                  <div className='cms-theme-mini-header'>
                    <span />
                    <i />
                    <i />
                  </div>
                  <div className='cms-theme-mini-hero'>
                    <div>
                      <b />
                      <b />
                      <i />
                    </div>
                    <span />
                  </div>
                  <div className='cms-theme-mini-cards'>
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
                <span className='cms-theme-label'>
                  <span>
                    <b>{theme.title}</b>
                    <small>{theme.caption}</small>
                  </span>
                  {draft.theme === theme.id && <Check size={19} />}
                </span>
              </button>
            ))}
          </div>
          {undo && undo.revision === draft.revision && (
            <button
              type='button'
              className='cms-link-button'
              onClick={() => {
                onChange({
                  ...draft,
                  theme: undo.theme,
                  branding: { ...brand, primary: undo.primary, accent: undo.accent, typography: undo.typography },
                });
                setUndo(null);
              }}
            >
              Annuler le changement d’ambiance
            </button>
          )}
        </section>
        <section className='cms-card'>
          <span className='cms-kicker'>Personnalisation</span>
          <h3>Couleurs & typographie</h3>
          <div className='cms-form-grid'>
            <Color label='Couleur principale' value={brand.primary} onChange={primary => update({ primary })} />
            <Color label='Couleur d’accent' value={brand.accent} onChange={accent => update({ accent })} />
            <label>
              Typographie
              <select
                value={brand.typography}
                onChange={e => update({ typography: e.target.value as SiteCmsBranding['typography'] })}
              >
                <option value='modern'>Moderne & lisible</option>
                <option value='editorial'>Éditoriale & chaleureuse</option>
                <option value='humanist'>Conviviale & expressive</option>
              </select>
            </label>
            <label>
              Forme des boutons
              <select
                value={brand.buttonShape}
                onChange={e => update({ buttonShape: e.target.value as SiteCmsBranding['buttonShape'] })}
              >
                <option value='rounded'>Arrondie</option>
                <option value='square'>Structurée</option>
                <option value='pill'>Pilule</option>
              </select>
            </label>
          </div>
          <p className='cms-hint'>
            Le texte des boutons s’adapte pour rester lisible. Les changements attendent votre enregistrement puis votre
            publication.
          </p>
        </section>
      </div>
      <section className='cms-card cms-design-preview'>
        <div className='cms-card-heading'>
          <h3>Voir le résultat</h3>
          <div className='cms-segmented' role='group' aria-label='Format de l’aperçu'>
            <button
              type='button'
              aria-pressed={!mobile}
              onClick={() => {
                setMobile(false);
                setPreview(true);
              }}
            >
              <Monitor size={17} />
              Ordinateur
            </button>
            <button
              type='button'
              aria-pressed={mobile}
              onClick={() => {
                setMobile(true);
                setPreview(true);
              }}
            >
              <Smartphone size={17} />
              Mobile
            </button>
          </div>
        </div>
        {preview && active ? (
          <SiteCmsPreview draft={draft} mobile={mobile} />
        ) : (
          <div className='cms-preview-placeholder'>
            <p>Le même rendu que votre futur site public, avec vos images privées.</p>
            <button type='button' className='cms-button cms-button-primary' onClick={() => setPreview(true)}>
              Afficher l’aperçu
            </button>
          </div>
        )}
        <p className='cms-hint'>Le thème du site ne modifie pas le dashboard BoulesConnect.</p>
      </section>
    </div>
  );
}
function Color({ label, value, onChange }: { label: string; value: string; onChange: (hex: string) => void }) {
  const id = useId();
  const [entry, setEntry] = useState<{ original: string; text: string } | null>(null);
  const candidate = entry?.original === value ? entry : null;
  const shown = candidate?.text ?? value;
  const valid = /^#[0-9a-f]{6}$/i.test(shown);
  useCmsPendingForm(`Code couleur — ${label}:${id}`, Boolean(candidate && !valid));
  return (
    <label>
      {label}
      <div className='cms-color-field'>
        <input
          type='color'
          aria-label={label}
          value={value}
          onChange={event => {
            setEntry(null);
            onChange(event.target.value);
          }}
        />
        <input
          aria-label={`Code HEX — ${label}`}
          aria-invalid={!valid}
          aria-describedby={`${id}-hint`}
          value={shown}
          maxLength={7}
          onChange={event => {
            if (/^#[0-9a-f]{6}$/i.test(event.target.value)) {
              onChange(event.target.value);
              setEntry(null);
            } else setEntry({ original: value, text: event.target.value });
          }}
        />
      </div>
      <small id={`${id}-hint`}>
        {valid
          ? 'Format : #123D69'
          : 'Code incomplet : six caractères hexadécimaux attendus. La couleur précédente reste appliquée.'}
      </small>
      {!valid && (
        <button type='button' className='cms-link-button' onClick={() => setEntry(null)}>
          Annuler la saisie de couleur
        </button>
      )}
    </label>
  );
}
